'use client';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from '@/components/Dialog';
import { Button } from '@/components/Button/ButtonV2';
import { AlertCircle, Copy, CheckCircle } from 'lucide-react';
import { getInitials, getAvatarColor, hasProfessionalSchedule } from '../utils';
import { Professional } from '@/containers/CustomerJourneySetup/context/types';

interface DialogCloneProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  professionals: Professional[];
  selectedProfessionalId: string;
  copyFromProfessionalId: string;
  setCopyFromProfessionalId: (id: string) => void;
  onCopySchedule: () => void;
}

export const DialogClone = ({
  isOpen,
  onOpenChange,
  professionals,
  selectedProfessionalId,
  copyFromProfessionalId,
  setCopyFromProfessionalId,
  onCopySchedule
}: DialogCloneProps) => {
  const eligibleProfessionals = professionals.filter(
    (p) => p.id !== selectedProfessionalId && hasProfessionalSchedule(p)
  );

  // Check if the selected professional already has schedules
  const selectedProfessional = professionals.find((p) => p.id === selectedProfessionalId);
  const selectedProfessionalHasSchedules = selectedProfessional
    ? hasProfessionalSchedule(selectedProfessional)
    : false;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] sm:max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="border-b pb-4 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <Copy className="h-5 w-5 text-primary" />
            Copiar horarios recurrentes
          </DialogTitle>
          <DialogClose />
        </DialogHeader>

        <div className="overflow-y-auto flex-1 p-6">
          {eligibleProfessionals.length > 0 ? (
            <div className="space-y-5">
              <p className="text-sm font-medium">
                Selecciona el profesional del cual quieres copiar los horarios:
              </p>

              <div className="grid gap-3">
                {eligibleProfessionals.map((professional) => {
                  const avatarColor = getAvatarColor(professional.id);
                  const isSelected = copyFromProfessionalId === professional.id;

                  return (
                    <button
                      key={professional.id}
                      className={`w-full flex items-center justify-between p-4 rounded-lg transition-all ${
                        isSelected
                          ? 'bg-primary/10 border-2 border-primary'
                          : 'bg-white border border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => setCopyFromProfessionalId(professional.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-medium ${avatarColor} text-white`}
                        >
                          {getInitials(`${professional.first_name} ${professional.last_name}`)}
                        </div>
                        <div className="flex flex-col items-start">
                          <span className="font-medium">
                            {professional.first_name} {professional.last_name}
                          </span>
                        </div>
                      </div>

                      {isSelected && <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>

              {selectedProfessionalHasSchedules && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-700">
                    <p className="font-medium mb-1">Importante</p>
                    <p>Esta acción sobrescribirá todos los horarios actuales del profesional seleccionado.</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="mx-auto bg-gray-100 rounded-full h-12 w-12 flex items-center justify-center mb-3">
                <AlertCircle className="h-6 w-6 text-gray-500" />
              </div>
              <h3 className="text-lg font-medium mb-2">No hay profesionales disponibles</h3>
              <p className="text-sm text-gray-500">
                No existen otros profesionales con horarios configurados para copiar.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="border-t p-6 flex-shrink-0 flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            Cancelar
          </Button>
          <Button
            onClick={onCopySchedule}
            disabled={!copyFromProfessionalId}
            className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
          >
            <div className="flex items-center gap-2">
              <Copy className="h-4 w-4" />
              Copiar horarios
            </div>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
